/* eslint-disable react-hooks/exhaustive-deps */
import {useEffect, useMemo, useState} from "react"
import {Col, Row, Tooltip} from "antd"
import {courseService, taskService} from "services"
import {
    BaseNewFilter,
    KlassappTable,
    KlassappTableHeader,
    ChangeCourseStatusPopup,
    AssignTaskPopup,
    AssignMultiTaskPopup
} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {formatDecimal, getFullName, handleError} from "helpers"
import {useModel, useVisible} from "hooks"
import {find, get, head, isEmpty, uniqBy} from "lodash"
import {useHistory} from "react-router-dom"
import {routing} from "helpers/routing"
import {BaseButton, Icon} from "components"
import {KlassappTableProps} from "types/common"
import styles from "./StudentsTab.module.css"
import {AddStudentPopup} from "./AddStudentPopup"
import {Course} from "types/courses"
import moment from "moment"
import {AddDropVariant} from "types/terms"
import {getDateOnly} from "sections/academics/instructional/common/utils"
import CampusSelect from "components/CampusSelect"
import {useQuery} from "@tanstack/react-query"
import {useUpdateEffect} from "react-use"

type Props = KlassappTableProps & {
    scheduleId: number
    activeSchedule: any
    courseId: number
    termId: number
    fromRoute: "registrar" | "instructional"
    courseInfo: Course.CourseTableItem
    courseSettings: any
}

function StudentsTab(props: Props) {
    const {
        dispatch,
        dispatchFunc,
        page,
        total,
        pageSize,
        columns,
        data,
        allFields,
        fields,
        tableHeaderActions,
        menuActions,
        scheduleId,
        orderField,
        isLoading,
        isShowTableHeaderAction,
        courseInfo,
        isLoadedTableFuncs,
        courseId,
        termId,
        activeSchedule,
        courseSettings,
        fromRoute
    } = props
    const history = useHistory()
    const model = useModel()
    const addStudentPopup = useVisible(false)
    const changeCourseStatusPopup = useVisible(false)
    const assignTaskPopup = useVisible(false)
    const [filter, setFilter] = useState({campuses: null})
    const [formFilter, setFormFilter] = useState({campuses: null})
    const [search, setSearch] = useState<string>()
    const [activeCourseStatus, setActiveCourseStatus] = useState(null)
    const assignMultiTaskPopup = useVisible(false)
    const [checkedStudents, setCheckedStudents] = useState([])
    const [relatedTaskIds, setRelatedTaskIds] = useState([])
    const [currentCustomTasks, setCurrentCustomTasks] = useState([])

    const studentProfileIds = data.map((item) => item.student.profileId)

    const filters = useMemo(() => {
        const result: any = {courseIds: [courseId]}
        if (termId) {
            result.termIds = [termId]
        }
        if (!isEmpty(filter.campuses)) {
            result.campusIds = filter.campuses.map((item) => item.id)
        }
        if (search && search !== "") {
            result.search = search
        }
        if (!model.isStaffOrAdmin() && model.profileId) {
            result.studentProfileIds = [model.profileId]
        }
        if (scheduleId) {
            result.scheduleId = scheduleId
        }
        return result
    }, [courseId, termId, filter.campuses, search, model.profileId, scheduleId])

    const {
        data: {data: instructionalStudentsList, total: totalStudents},
        isFetching: isLoadingStudents,
        refetch: refetchStudents
    } = useQuery(
        ["instructional-students-list", filters, page, pageSize, orderField],
        async () => {
            const params: any = {range: {page, pageSize}, filter: filters}
            if (!isEmpty(orderField)) {
                params.sort = {orderBy: orderField.field, orderDir: orderField.order}
            }
            return await courseService.listInstructionalStudents(params)
        },
        {onError: (err) => handleError(err), initialData: {data: [], total: 0}}
    )

    useEffect(() => {
        if (!isLoadingStudents) {
            getStudentTableData()
        }
    }, [instructionalStudentsList, isLoadingStudents])

    useEffect(() => {
        dispatch({isLoading: isLoadingStudents})
    }, [isLoadingStudents])

    useEffect(() => {
        dispatchFunc([{key: "getMenuActions", func: getMenuActions}])
    }, [scheduleId])

    useUpdateEffect(() => {
        getStudentTableData(currentCustomTasks)
    }, [relatedTaskIds])

    useEffect(() => {
        props.updateTableHeaderActions()
    }, [data])

    const getStudentTableData = async (currentCustomTasks = null) => {
        const profileIds = instructionalStudentsList.map((item) => item.student.profileId)
        if (!isEmpty(profileIds)) {
            const customTasks = !currentCustomTasks
                ? activeSchedule?.id
                    ? await taskService.getCustomDigitalTasksOfStudent({
                          filter: {
                              profileIds,
                              courseIds: [courseId],
                              termIds: termId ? [termId] : undefined,
                              scheduleIds: activeSchedule?.id ? [activeSchedule.id] : undefined
                          }
                      })
                    : []
                : currentCustomTasks
            if (!currentCustomTasks) {
                setCurrentCustomTasks(customTasks)
            }
            const result = instructionalStudentsList.map((item: any) => {
                const {profileId, termId, courseId, scheduleId} = item.student
                item.id = `${profileId}${termId}${courseId}${scheduleId}`
                item.profileId = item.student.profileId
                item.fullName = renderName(item.student)
                item.tasksHtml = renderTasks(item.student, customTasks)
                item.customProfileId = item.student.customProfileId
                item.scheduleName = item.student.scheduleName
                item.statusName = item.student?.statusName ?? "Not Defined"
                item.gpa = formatDecimal(item.student?.gpa)
                item.academicStatus = item.student?.academicStatus ?? "Not Defined"
                item.percentage = `${formatDecimal(parseFloat(get(item, ["student", "percentage"]) ?? 0))}%`
                item.isRegistered = !!item.student?.isRegistered ? <Icon icon="CHECKED" /> : null
                return item
            })
            dispatch({data: result, total: totalStudents, isLoading: false})
        } else {
            dispatch({data: [], total: 0, isLoading: false})
        }
    }

    const getFields = () => {
        const fields = [
            "ID",
            "Name",
            "Last Lesson",
            "Grade (GPA)",
            "Attendance",
            "Is Registered",
            "Schedule",
            "Tasks",
            "Student Status"
        ]
        return fields
    }

    const getPageTitle = () => {
        return "Student"
    }

    const getTableHeaderActions = (_, checkedData) => {
        return [
            {
                title: "Assign Task",
                icon: "DIGITAL_DOCUMENT_TASK",
                action: () => {
                    const students = checkedData.map((item) => ({
                        profileId: item.student.profileId,
                        courseId: item.student.courseId,
                        scheduleId: item.student.scheduleId,
                        termId: item.student.termId
                    }))
                    const uniqStudents = uniqBy(
                        students,
                        (item: any) => `${item.profileId}_${item.courseId}_${item.scheduleId}_${item.termId}`
                    )
                    setCheckedStudents(uniqStudents)
                    assignMultiTaskPopup.open()
                }
            }
        ]
    }

    const getColumns = () => {
        const columns = [
            {
                title: "ID",
                field: "customProfileId",
                orderField: "customProfileId",
                sortable: true
            },
            {
                title: "Name",
                field: "fullName",
                sortable: true,
                style: {minWidth: "200px"}
            },
            {
                title: "Last Lesson",
                field: "lda",
                fieldType: "date",
                format: "MM/DD/YYYY"
            },
            {
                title: "Grade (GPA)",
                field: "gpa"
            },
            {
                title: "Attendance",
                field: "percentage"
            },
            {
                title: "Is Registered",
                field: "isRegistered",
                sortable: true
            },
            {
                title: "Schedule",
                field: "scheduleName",
                sortable: true
            },
            {
                title: "Tasks",
                field: "tasksHtml",
                style: {minWidth: 200},
                sortable: true
            },
            {
                title: "Student Status",
                field: "statusName",
                sortable: true
            }
        ]
        return columns
    }

    const onClickChangeCourseStatus = (item) => {
        setActiveCourseStatus(item)
        changeCourseStatusPopup.open()
    }

    const clickAssignTask = (item) => {
        setActiveCourseStatus(item)
        assignTaskPopup.open()
    }

    const getMenuActions = () => {
        const actions = [
            {
                title: "Change Status",
                icon: "EDIT",
                action: onClickChangeCourseStatus
            }
        ]
        if (!isEmpty(courseSettings?.edularTasks)) {
            actions.push({title: "Assign task", icon: "DIGITAL_DOCUMENT_TASK", action: clickAssignTask})
        }
        return actions
    }

    const renderName = (item) => {
        const isDropBeforeAddDrop =
            item.addDropVariant === AddDropVariant.Date
                ? moment(item.droppedDate).isSameOrBefore(moment(item.addDropDate))
                : moment(item.droppedDate).isSameOrBefore(moment(item.addDropHours))

        return (
            <div className={styles.name}>
                <div className={styles.nameStudent}>
                    <span>{getFullName(item)}</span>
                </div>
                {!!item.isMakeup && (
                    <div className={styles.icon}>
                        <Icon icon="PLUS_PERSON" color="#666" />
                    </div>
                )}
                {!!item.isDropped && (
                    <p className={styles.droppedText} style={{color: isDropBeforeAddDrop ? "#1890ff" : "red"}}>
                        DD (
                        {getDateOnly({
                            date: item.droppedDate,
                            placeholder: true,
                            dateFormat: model.getUserDateFormat()
                        })}
                        )
                    </p>
                )}
                {!!item.isLoa && (
                    <p className={styles.droppedText} style={{color: "#1890ff"}}>
                        LOA (
                        {getDateOnly({
                            date: item.statusUpdatedAt,
                            placeholder: true,
                            dateFormat: model.getUserDateFormat()
                        })}
                        )
                    </p>
                )}
            </div>
        )
    }

    const renderTasks = (item, customTasks) => {
        const assignedTasksByStudent =
            customTasks && find(customTasks, (customTask) => customTask.profileId === item.studentProfileId)
        const assignedTasks = get(assignedTasksByStudent, "tasks", [])
        return (
            <div className={styles.assignTaskWrap}>
                {assignedTasks.map(({taskId, name, isApproved}) => (
                    <Tooltip key={taskId} title="Task answer">
                        <div
                            className={styles.assignedTask}
                            onClick={(event) => {
                                event.stopPropagation()
                                setRelatedTaskIds((prev) =>
                                    !prev.includes(taskId) ? [...prev, taskId] : prev.filter((item) => item !== taskId)
                                )
                            }}
                            onDoubleClick={(event) => {
                                event.stopPropagation()
                                history.push(`/tasks/submissions?id=${taskId}&type=customDD`)
                            }}>
                            <span>
                                {name} ({taskId})
                            </span>
                            <div className={styles.assignIconWrap}>
                                <Icon
                                    icon="ARROW_RIGHT_CIRCLE"
                                    color={relatedTaskIds.includes(taskId) ? "#ff8a38" : "#1E90FF"}
                                    className={styles.iconDrop}
                                />
                            </div>
                            {isApproved && <Icon icon="CHECKMARK_CIRCLE" className={styles.iconDrop} color="#18A957" />}
                        </div>
                    </Tooltip>
                ))}
            </div>
        )
    }

    const onClickRowItem = (row) => {
        if (fromRoute === "registrar") {
            history.push(routing.registrar.courseStudentDetail(courseId, termId, row.profileId))
        } else if (scheduleId) {
            history.push(
                routing.academics.instructional.creditCourseScheduleStudentDetail(
                    courseId,
                    termId,
                    row.profileId,
                    scheduleId
                )
            )
        } else {
            history.push(routing.academics.instructional.creditCourseStudentDetail(courseId, termId, row.profileId))
        }
    }

    const onClickDeleteMulti = async () => {}

    const onClickDuplicateMulti = async () => {}

    const onClickEdit = () => {}

    const onClickDelete = async () => {}

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getFields", func: getFields},
            {key: "getTableHeaderActions", func: getTableHeaderActions},
            {key: "getColumns", func: getColumns},
            {key: "onClickDeleteMulti", func: onClickDeleteMulti},
            {key: "onClickDuplicateMulti", func: onClickDuplicateMulti},
            {key: "onClickEdit", func: onClickEdit},
            {key: "onClickDelete", func: onClickDelete},
            {key: "onClickRowItem", func: onClickRowItem}
        ])
    }, [])

    const onChangeFilter = (key, value) => {
        const newFilter = {...filter}
        newFilter[key] = value
        setFormFilter(newFilter)
    }

    const onApplyFilter = () => {
        setFilter({...formFilter})
    }

    const onClickClearFilter = () => {
        setFilter({campuses: null})
        setFormFilter({campuses: null})
    }

    const onSearchInput = (text) => {
        setSearch(text)
    }

    const onCloseChangeCourseStatus = () => {
        changeCourseStatusPopup.close()
        setActiveCourseStatus(null)
        refetchStudents()
    }

    const onCloseAssignTask = () => {
        assignTaskPopup.close()
        setActiveCourseStatus(null)
        refetchStudents()
    }

    const onCloseAssignMultiTask = () => {
        assignMultiTaskPopup.close()
        setCheckedStudents([])
        refetchStudents()
    }

    const renderAddButton = () => {
        if (isEmpty(activeSchedule)) {
            return null
        }
        return (
            <div className={styles.addStudentBox}>
                <BaseButton
                    title="Task submissions"
                    disabled={isEmpty(relatedTaskIds)}
                    onClick={() => {
                        history.push({
                            pathname: `/tasks/submissions`,
                            search: `id=${head(relatedTaskIds)}&type=customDD&relatedTaskIds=${relatedTaskIds}`,
                            state: {
                                backUrl: `${history.location.pathname}${history.location.search}`
                            }
                        })
                    }}
                />
                <BaseButton title="Add Student" onClick={addStudentPopup.open} />
            </div>
        )
    }

    return (
        <div>
            <BaseNewFilter
                filter={formFilter}
                onClick={onApplyFilter}
                onClickClear={onClickClearFilter}
                renderRightFilter={() => renderAddButton()}
                onSearchInput={onSearchInput}>
                <Row gutter={[24, 24]}>
                    <Col span={24}>
                        <CampusSelect
                            isMulti
                            placeholder="Campus"
                            value={formFilter.campuses}
                            onChange={(newValue) => onChangeFilter("campuses", newValue)}
                        />
                    </Col>
                </Row>
            </BaseNewFilter>
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                fields={fields}
                data={data}
                menuActions={menuActions}
                isLoading={isLoading}
                allFields={allFields}
                orderField={orderField}
                isShowCheckedColumn
                onClickRowItem={onClickRowItem}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onUpdateRowData={props.onUpdateRowData}
                onClickSortColumn={props.onClickSortColumn}
                onUpdateTableData={props.onUpdateTableData}
                onDraggableColumn={props.onDraggableColumn}
            />
            {addStudentPopup.isVisible && (
                <AddStudentPopup
                    studentProfileIds={studentProfileIds}
                    isShow={addStudentPopup.isVisible}
                    onClose={() => {
                        addStudentPopup.close()
                        refetchStudents()
                    }}
                    campusIds={courseInfo?.campusIds ?? []}
                    courseInfo={courseInfo}
                    termId={termId}
                    courseId={courseId}
                    activeSchedule={activeSchedule}
                />
            )}
            {changeCourseStatusPopup.isVisible && activeCourseStatus && (
                <ChangeCourseStatusPopup
                    isShow={changeCourseStatusPopup.isVisible}
                    activeCourseStatus={activeCourseStatus.student}
                    studentProfileId={activeCourseStatus.student.studentProfileId}
                    onSuccess={onCloseChangeCourseStatus}
                    onClose={onCloseChangeCourseStatus}
                />
            )}
            {activeCourseStatus && scheduleId && (
                <AssignTaskPopup
                    isShow={assignTaskPopup.isVisible}
                    onClose={onCloseAssignTask}
                    studentProfileId={activeCourseStatus.student.studentProfileId}
                    termId={activeCourseStatus.student.termId}
                    courseId={activeCourseStatus.student.courseId}
                    scheduleId={activeCourseStatus.student.scheduleId}
                    edularTasks={courseSettings.edularTasks}
                />
            )}
            {assignMultiTaskPopup.isVisible && (
                <AssignMultiTaskPopup
                    isShow={assignMultiTaskPopup.isVisible}
                    onClose={onCloseAssignMultiTask}
                    students={checkedStudents}
                    edularTasks={courseSettings.edularTasks}
                />
            )}
        </div>
    )
}

export default KlassappTableHOC(StudentsTab)
